import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { faBriefcase, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState, RootState } from '@share/utils';
import { getDashboarReports, IDashboardReportState } from '@store/slices';

import { MONTHLY } from '../../../dashboard/dashboard-report';
import { DashboardReportBookingsTotalBar } from '../../../dashboard/dashboard-report/dashboard-report-bookings-total';
import { DashboardReportFilters } from '../../../dashboard/dashboard-report/dashboard-report-filters';
import { AccountEmptyState } from '../empty-state';
import { AmountSavingsCard, ReportInsights } from './insights';

import styles from './styles.module.scss';

/**
 * My reports for travel agents: commissions at a glance on top, then My Dashboard's own report charts
 * and lists. Everything reads the same report (`/bookings/reporttravelagenthome`) and the same period
 * filter as My Dashboard, which redirects here when the hub is on. Read-only: the report has no
 * per-booking ledger, available balance or payouts, so there is no cash out yet.
 */
export const AccountReports = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const { dashBoardData, loading, error }: IDashboardReportState = useSelector(
    (state: FunctionalRootState) => state.dashboardReportsStore,
  );
  // Monthly / daily toggle shared by the bookings line chart and the pie, as on My Dashboard.
  const [period, setPeriod] = useState(MONTHLY);

  // The period filter below loads the report when it mounts; this only covers a custom range left
  // selected (the filter waits for dates then). Once only, so a failed request doesn't loop.
  const requested = useRef(false);
  useEffect(() => {
    if (!requested.current && !dashBoardData && !loading) {
      requested.current = true;
      dispatch(getDashboarReports());
    }
  }, [dispatch, dashBoardData, loading]);

  const money = (value: number | null | undefined) =>
    intl.formatNumber(value || 0, { style: 'currency', currency: 'USD' });

  const data = dashBoardData;
  const isEmpty = !!data && !data.commissions && !data.pendingCommissions && !data.sales && !data.confirmedBookings;

  const renderBody = () => {
    if (loading && !data) {
      return (
        <div className={styles.summary}>
          <div className={`${styles.hero} ${styles.heroLoading}`}><Skeleton active title={false} paragraph={{ rows: 2 }} /></div>
          {[0, 1].map(key => (
            <div key={key} className={styles.tile}><Skeleton active title={false} paragraph={{ rows: 2 }} /></div>
          ))}
        </div>
      );
    }

    if (error && !data) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.commissions.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.commissions.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: () => dispatch(getDashboarReports()) }}
        />
      );
    }

    return (
      <>
        <div className={styles.summary}>
          <section className={styles.hero} aria-labelledby="account-hub-commissions-earned">
            <span id="account-hub-commissions-earned" className={styles.heroLabel}>
              {intl.formatMessage({ id: 'my.account.hub.commissions.earned' })}
            </span>
            <span className={styles.heroValue}>{money(data?.commissions)}</span>
            <span className={styles.heroNote}>
              {intl.formatMessage({ id: 'my.account.hub.commissions.earned_note' }, { count: data?.confirmedBookings || 0 })}
            </span>
          </section>

          <div className={styles.tile}>
            <span className={styles.tileLabel}>{intl.formatMessage({ id: 'my.account.hub.commissions.pending' })}</span>
            <span className={styles.tileValue}>{money(data?.pendingCommissions)}</span>
            <span className={styles.tileNote}>{intl.formatMessage({ id: 'my.account.hub.commissions.pending_note' })}</span>
          </div>

          <div className={styles.tile}>
            <span className={styles.tileLabel}>{intl.formatMessage({ id: 'my.account.hub.commissions.sales' })}</span>
            <span className={styles.tileValue}>{money(data?.sales)}</span>
            <span className={styles.tileNote}>
              {intl.formatMessage({ id: 'my.account.hub.commissions.savings_note' }, { amount: money(data?.savings) })}
            </span>
          </div>
        </div>

        <dl className={styles.facts}>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.commissions.bookings' })}</dt>
            <dd>{intl.formatNumber(data?.confirmedBookings || 0)}</dd>
          </div>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.commissions.cancellations' })}</dt>
            <dd>{intl.formatNumber(data?.canceledBookings || 0)}</dd>
          </div>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.reports.rooms_title' })}</dt>
            <dd>{intl.formatNumber((data?.roomsBookedMonths || []).reduce((total, entry) => total + (entry.numberOfRooms || 0), 0))}</dd>
          </div>
        </dl>

        {isEmpty ? (
          <AccountEmptyState
            variant="panel"
            icon={faBriefcase}
            title={intl.formatMessage({ id: 'my.account.hub.commissions.empty_title' })}
            body={intl.formatMessage({ id: 'my.account.hub.commissions.empty_body' })}
          />
        ) : (
          // My Dashboard's two booking charts (restyled by `.charts`), then compact insight cards.
          <div className={styles.charts}>
            <div className={styles.chartRow}>
              <div className={styles.chartWide}>
                <DashboardReportBookingsTotalBar period={period} onPeriodChange={setPeriod} />
              </div>
              <div className={styles.chartNarrow}>
                {data ? <AmountSavingsCard data={data} period={period} /> : null}
              </div>
            </div>
            {data ? <ReportInsights data={data} /> : null}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={loading && data ? `${styles.reports} ${styles.refreshing}` : styles.reports} aria-busy={loading}>
      {/* Kept outside the states above so the filter never remounts (it fetches when it mounts). */}
      <div className={styles.toolbar}>
        <DashboardReportFilters />
      </div>
      {renderBody()}
    </div>
  );
};
